import type { QueryClient } from '@tanstack/react-query';
import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { shouldUseGobodo, USE_GOBODO_API } from '../../utils/gobodoUtils';

const customSuffix = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const RECORD_TYPE_OBJECT = `record_type${customSuffix}`;
const CREDIT_BLOCK_RECORD_TYPE_ID = (
  process.env.REACT_APP_CREDIT_BLOCK_RECORD_TYPE_ID || ''
).trim();

/** Display name on synced Record Type rows; override if Gobodo/SF label differs (must match indexed Name__c). */
const CREDIT_BLOCK_RECORD_TYPE_NAME_LABEL = (
  process.env.REACT_APP_CREDIT_BLOCK_RECORD_TYPE_NAME || 'Credit Block'
).trim();
export const CREDIT_BLOCK_RECORD_TYPE_TARGET_STALE_MS = 60 * 60 * 1000;
const CREDIT_BLOCK_RECORD_TYPE_TARGET_CACHE_DURATION_SECONDS = 86400;
const CREDIT_BLOCK_RECORD_TYPE_TARGET_CACHE_TAG = 'v2-key-sfid-only';
const CREDIT_BLOCK_RECORD_TYPE_TARGET_DATA_FIELDS = [
  `sf_id${customSuffix}`,
  'sf_id',
  'SfId__c',
  'Id__c',
].join(',');
export const creditBlockRecordTypeTargetQueryKey = [
  'credit-block',
  'record-type-target',
  RECORD_TYPE_OBJECT,
  CREDIT_BLOCK_RECORD_TYPE_NAME_LABEL,
  CREDIT_BLOCK_RECORD_TYPE_ID || 'lookup',
] as const;

function stripSuffix(value: string): string {
  return value.replace(/__stg3_c$|__stg_c$|__dev_c$|__c$/i, '');
}

function normalizeConfiguredRecordTypeSfId(value: string): string | null {
  const raw = stripSuffix(String(value ?? '').trim());
  return /^012[A-Za-z0-9]{12}([A-Za-z0-9]{3})?$/.test(raw) ? raw : null;
}

function getConfiguredCreditBlockRecordTypeTarget(): CreditBlockRecordTypeTarget | null {
  const sfId = normalizeConfiguredRecordTypeSfId(CREDIT_BLOCK_RECORD_TYPE_ID);
  if (!sfId) return null;
  return {
    objectKey: `${sfId}${customSuffix}`,
    sfId,
    label: CREDIT_BLOCK_RECORD_TYPE_NAME_LABEL || 'Credit Block',
  };
}

/** Extract Salesforce Record Type Id (prefix 012...) from a synced record_type Gobodo object. */
function extractRecordTypeSfIdFromGobodoObject(obj: unknown): string | null {
  if (obj == null || typeof obj !== 'object') return null;
  const r = obj as Record<string, unknown>;
  const d = (r.data as Record<string, unknown>) ?? r;
  const sfIdSuffixed = `sf_id${customSuffix}`;
  for (const value of [d[sfIdSuffixed], r[sfIdSuffixed], d.sf_id, r.sf_id, d.SfId__c, r.SfId__c, d.Id__c, r.Id__c]) {
    const raw = String(value ?? '').trim();
    if (!raw) continue;
    const bare = stripSuffix(raw);
    if (/^012[A-Za-z0-9]{12}([A-Za-z0-9]{3})?$/.test(bare)) {
      return bare;
    }
  }
  return null;
}

export interface CreditBlockRecordTypeTarget {
  objectKey: string;
  sfId: string | null;
  label: string;
}

type CreditBlockRecordTypeReadOpts = {
  cachekey: string[];
  cacheduration: string;
  objectSections: string;
  dataFields: string;
  maxObjectTypeFields: number;
  includeExpandedFieldData: boolean;
  includeRelatedMedia: boolean;
};

function buildCreditBlockRecordTypeReadOpts(): CreditBlockRecordTypeReadOpts {
  return {
    cachekey: [
      'credit-block-record-type-target',
      CREDIT_BLOCK_RECORD_TYPE_TARGET_CACHE_TAG,
      RECORD_TYPE_OBJECT,
      CREDIT_BLOCK_RECORD_TYPE_NAME_LABEL,
    ],
    cacheduration: String(CREDIT_BLOCK_RECORD_TYPE_TARGET_CACHE_DURATION_SECONDS),
    objectSections: 'data',
    dataFields: CREDIT_BLOCK_RECORD_TYPE_TARGET_DATA_FIELDS,
    maxObjectTypeFields: 0,
    includeExpandedFieldData: false,
    includeRelatedMedia: false,
  };
}

/**
 * Resolve the Credit Block record_type object by Name__c so traversal callers can filter
 * Credit Block rows by the same canonical Record Type target.
 */
export async function fetchCreditBlockRecordTypeTarget(): Promise<CreditBlockRecordTypeTarget | null> {
  if (!USE_GOBODO_API || !shouldUseGobodo()) return null;
  const configuredTarget = getConfiguredCreditBlockRecordTypeTarget();
  if (configuredTarget) return configuredTarget;
  const label = CREDIT_BLOCK_RECORD_TYPE_NAME_LABEL;
  if (!label) return null;
  try {
    const apiClient = getGobodoClient();
    const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
    const input = new GobodoApi.GraphTraversalInput();
    input.setGraphId(GobodoConfig.graph());
    input.setStartObjectTypes([RECORD_TYPE_OBJECT]);
    input.setEndObjectTypes([RECORD_TYPE_OBJECT]);
    input.setMaxPathLength(0);
    const nameFilter = new GobodoApi.IndexFilter();
    nameFilter.setIndexName('Name__c');
    nameFilter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
    nameFilter.setValues([label]);
    input.setStartObjectFieldFilters([nameFilter]);
    const result = await retryWithBackoff(
      () => traversalApi.traverseObjects(input, buildCreditBlockRecordTypeReadOpts()),
      { maxRetries: 2, initialDelayMs: 1000 }
    );
    if (!Array.isArray(result) || result.length === 0) return null;
    const first = result[0] as Record<string, unknown>;
    const data = (first.data as Record<string, unknown>) ?? first;
    const objectKey = String(first.key ?? data.key ?? '').trim();
    if (!objectKey) return null;
    return {
      objectKey,
      sfId: extractRecordTypeSfIdFromGobodoObject(first),
      label: String(first.label ?? data.Name__c ?? label).trim() || label,
    };
  } catch {
    return null;
  }
}

export function fetchCreditBlockRecordTypeTargetCached(queryClient: QueryClient) {
  return queryClient.fetchQuery({
    queryKey: creditBlockRecordTypeTargetQueryKey,
    queryFn: fetchCreditBlockRecordTypeTarget,
    staleTime: CREDIT_BLOCK_RECORD_TYPE_TARGET_STALE_MS,
  });
}
